// File drop zone for components/RequestLinesForm.jsx — entry 45's "merge
// the two entry paths into this one form": a buyer can attach a BOQ/
// spreadsheet (parsed automatically if it's a real .xlsx — see backend/src/
// services/requestUploadService.js) or a PDF (attached only, sales reads it
// and enters lines by hand) before or instead of typing manual line rows.
//
// Controlled from the parent (RequestLinesForm holds the actual `File`
// object in its own state, not react-hook-form — a raw File isn't
// meaningfully "registered" the way a text field is, and it can't survive
// this app's own localStorage draft persistence either way, see that
// file's own comment). This component only owns the drag/drop and
// file-picker mechanics, plus client-side type checking for fast feedback
// (the server re-validates regardless, same relationship every other form
// in this app has with its own backend).
//
// Keyboard/click target: the whole dashed box is a <label> wrapping a
// visually-hidden <input type="file">, so clicking anywhere in it (not just
// the "Choose file" text) opens the file picker, and it's reachable by Tab
// like any native file input — no custom keyboard handling needed.
//
// forwardRef exposes the hidden <input> itself so a parent can call
// `.focus()` on it (RequestLinesForm's own consumePendingUploadFocus effect,
// for the landing page's "Upload BOQ or PDF" hero button — see utils/
// pendingUploadFocus.js).

import { forwardRef, useState } from 'react';
import { Paperclip, Upload, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext.jsx';
import styles from './UploadDropZone.module.css';

export const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv', '.pdf'];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(',');

function hasAcceptedExtension(filename) {
  const lower = (filename || '').toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

const UploadDropZone = forwardRef(function UploadDropZone(
  { file, onFileSelect, onFileRemove, error },
  ref
) {
  const { t } = useLanguage();
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState(null);

  const acceptOrReject = (candidate) => {
    if (!candidate) return;
    if (!hasAcceptedExtension(candidate.name)) {
      setLocalError(t('portal.requestForm.upload.invalidType'));
      return;
    }
    setLocalError(null);
    onFileSelect(candidate);
  };

  const handleInputChange = (e) => {
    acceptOrReject(e.target.files?.[0] || null);
    // Reset so picking the SAME file again after a remove still fires
    // onChange (the browser otherwise treats it as no change).
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    acceptOrReject(e.dataTransfer.files?.[0] || null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const shownError = localError || error;

  return (
    <div className={styles.wrapper}>
      <span className={styles.label}>{t('portal.requestForm.upload.title')}</span>

      {file ? (
        <div className={styles.selectedFile}>
          <Paperclip size={18} aria-hidden="true" />
          <span className={styles.filename}>{file.name}</span>
          <button
            type="button"
            className={styles.removeButton}
            aria-label={t('portal.requestForm.upload.remove')}
            onClick={onFileRemove}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
      ) : (
        <label
          className={`${styles.dropZone} ${isDragging ? styles.dragging : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Upload size={22} aria-hidden="true" />
          <span className={styles.dropText}>
            {t('portal.requestForm.upload.dropHint')}{' '}
            <span className={styles.chooseFileLink}>
              {t('portal.requestForm.upload.chooseFile')}
            </span>
          </span>
          <span className={styles.acceptedTypes}>
            {t('portal.requestForm.upload.acceptedTypes')}
          </span>
          <input
            ref={ref}
            type="file"
            accept={ACCEPT_ATTR}
            className={styles.hiddenInput}
            onChange={handleInputChange}
          />
        </label>
      )}

      {file && <p className={styles.hint}>{t('portal.requestForm.upload.hint')}</p>}
      {shownError && <p className={styles.fieldError}>{shownError}</p>}
    </div>
  );
});

export default UploadDropZone;
